import React, { useCallback, useMemo, useState } from 'react';
import { Download, Upload, Info, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { db, GoalTemplate, TaskTemplate, Task, Goal, TimeEntry } from '../../lib/db';

type Props = {
  userId: string;
};

// Simple helper to trigger a JSON download in the browser.
// This keeps everything client-side so users can export without any server steps.
function downloadJson(filename: string, data: any) {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Parse a user-provided file as JSON. Shows a friendly toast if parsing fails.
async function readJsonFile(file: File): Promise<any | null> {
  try {
    const text = await file.text();
    return JSON.parse(text);
  } catch (err) {
    console.error('Failed to parse JSON file', err);
    toast.error('Could not read that file. Please choose a valid JSON backup.');
    return null;
  }
}

// Component renders three simple cards: export/import deliverable templates, export/import regular tasks,
// and export/import full user data. Each button does all the work and explains what will happen.
export function ExportImportSettings({ userId }: Props) {
  const [busy, setBusy] = useState(false);
  const [fileA, setFileA] = useState<File | null>(null);
  const [fileB, setFileB] = useState<File | null>(null);
  const [fileC, setFileC] = useState<File | null>(null);

  const timestamp = useMemo(() => new Date().toISOString().replace(/[:.]/g, '-'), []);

  // ============ A) Deliverable Templates (Goal + Task Templates) ============
  const handleExportTemplates = useCallback(async () => {
    try {
      setBusy(true);
      // Goal (deliverable) templates created by this user
      const goalTemplates = await db.goalTemplates.findByUserId(userId);
      const taskTemplates: Record<string, TaskTemplate[]> = {};
      for (const gt of goalTemplates) {
        taskTemplates[gt.id] = await db.taskTemplates.findByGoalTemplateId(gt.id);
      }

      downloadJson(`goalie4-templates-${timestamp}.json`, {
        kind: 'templates',
        version: 1,
        exportedAt: new Date().toISOString(),
        goalTemplates,
        taskTemplates,
      });
      toast.success('Template deliverables exported');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export templates');
    } finally {
      setBusy(false);
    }
  }, [timestamp, userId]);

  const handleImportTemplates = useCallback(async () => {
    if (!fileA) {
      toast.error('Choose a JSON file to import');
      return;
    }
    try {
      setBusy(true);
      const data = await readJsonFile(fileA);
      if (!data || data.kind !== 'templates') {
        toast.error('This file does not look like a templates export');
        return;
      }

      const oldToNewTemplateId = new Map<string, string>();

      // Recreate goal templates for this user
      const gts: GoalTemplate[] = data.goalTemplates || [];
      for (const gt of gts) {
        const created = await db.goalTemplates.create({
          title: gt.title,
          description: gt.description,
          category: gt.category,
          is_system: false, // Imported items are user-owned, not system templates
          user_id: userId,
        });
        oldToNewTemplateId.set(gt.id as string, created.id);
      }

      // Recreate task templates that belong to each goal template
      const tts: Record<string, TaskTemplate[]> = data.taskTemplates || {};
      for (const [oldGtId, ttList] of Object.entries(tts)) {
        const newGtId = oldToNewTemplateId.get(oldGtId);
        if (!newGtId) continue;
        for (const tt of ttList) {
          await db.taskTemplates.create({
            goal_template_id: newGtId,
            title: tt.title,
            description: tt.description,
            priority: tt.priority,
            position: tt.position,
            user_id: userId,
            depends_on_positions: tt.depends_on_positions || [],
            dependency_type: tt.dependency_type || 'none',
            dependency_group: tt.dependency_group || null,
            offset_from_position: tt.offset_from_position,
            offset_days: tt.offset_days || 0,
            offset_type: tt.offset_type || 'none',
          });
        }
      }

      toast.success('Templates imported successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to import templates');
    } finally {
      setBusy(false);
      setFileA(null);
    }
  }, [fileA, userId]);

  // ============ B) Regular Tasks Only ============
  const handleExportRegular = useCallback(async () => {
    try {
      setBusy(true);
      const tasks = await db.tasks.findRegularTasks(userId);
      // Minimal associated history for context (best-effort)
      // We keep it simple: consumers may ignore if not present
      downloadJson(`goalie4-regular-tasks-${timestamp}.json`, {
        kind: 'regular-tasks',
        version: 1,
        exportedAt: new Date().toISOString(),
        tasks,
      });
      toast.success('Regular tasks exported');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export regular tasks');
    } finally {
      setBusy(false);
    }
  }, [timestamp, userId]);

  const handleImportRegular = useCallback(async () => {
    if (!fileB) {
      toast.error('Choose a JSON file to import');
      return;
    }
    try {
      setBusy(true);
      const data = await readJsonFile(fileB);
      if (!data || data.kind !== 'regular-tasks') {
        toast.error('This file does not look like a regular tasks export');
        return;
      }

      const tasks: Task[] = data.tasks || [];
      for (const t of tasks) {
        // Import as a fresh regular task for this user.
        // We intentionally drop goal links to avoid foreign key issues when goals are not present.
        await db.tasks.create({
          goal_id: null,
          user_id: userId,
          title: t.title,
          description: t.description,
          priority: t.priority || 'Medium',
          status: 'To Do',
          task_type: t.task_type || 'To Do',
          assigned_to: null,
          deadline: t.deadline || null,
          start_date: t.start_date ? new Date(t.start_date) : new Date(),
          end_date: null,
          position: t.position || 0,
          is_regular: true,
          frequency: t.frequency || 'Daily',
          last_generated_date: null,
          next_due_date: t.next_due_date || null,
          recurrence_day: t.recurrence_day || null,
          depends_on: [],
          is_live: true,
          dependency_type: 'none',
          dependency_group: null,
          sop: null,
        } as any);
      }

      toast.success('Regular tasks imported');
    } catch (err) {
      console.error(err);
      toast.error('Failed to import regular tasks');
    } finally {
      setBusy(false);
      setFileB(null);
    }
  }, [fileB, userId]);

  // ============ C) Full User Data (templates, deliverables, tasks, time entries) ============
  const handleExportAll = useCallback(async () => {
    try {
      setBusy(true);
      const goalTemplates = await db.goalTemplates.findByUserId(userId);
      const taskTemplates: Record<string, TaskTemplate[]> = {};
      for (const gt of goalTemplates) {
        taskTemplates[gt.id] = await db.taskTemplates.findByGoalTemplateId(gt.id);
      }

      const goals = await db.goals.findByUserId(userId, true);
      const tasks = await db.tasks.findByUserId(userId);

      // Collect time entries for each task owned by the user
      const timeEntries: Record<string, TimeEntry[]> = {};
      for (const task of tasks) {
        try {
          const entries = await db.timeEntries.findByTaskId(task.id, userId);
          if (entries && entries.length) timeEntries[task.id] = entries;
        } catch {
          // Best-effort; skip if query not supported
        }
      }

      downloadJson(`goalie4-all-data-${timestamp}.json`, {
        kind: 'all-data',
        version: 1,
        exportedAt: new Date().toISOString(),
        goalTemplates,
        taskTemplates,
        goals,
        tasks,
        timeEntries,
      });
      toast.success('All user data exported');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export data');
    } finally {
      setBusy(false);
    }
  }, [timestamp, userId]);

  const handleImportAll = useCallback(async () => {
    if (!fileC) {
      toast.error('Choose a JSON file to import');
      return;
    }
    try {
      setBusy(true);
      const data = await readJsonFile(fileC);
      if (!data || data.kind !== 'all-data') {
        toast.error('This file does not look like a full data export');
        return;
      }

      // 1) Templates
      const oldToNewGt = new Map<string, string>();
      const gts: GoalTemplate[] = data.goalTemplates || [];
      for (const gt of gts) {
        const created = await db.goalTemplates.create({
          title: gt.title,
          description: gt.description,
          category: gt.category,
          is_system: false,
          user_id: userId,
        });
        oldToNewGt.set(gt.id as string, created.id);
      }

      const tts: Record<string, TaskTemplate[]> = data.taskTemplates || {};
      for (const [oldGtId, list] of Object.entries(tts)) {
        const newGtId = oldToNewGt.get(oldGtId);
        if (!newGtId) continue;
        for (const tt of list) {
          await db.taskTemplates.create({
            goal_template_id: newGtId,
            title: tt.title,
            description: tt.description,
            priority: tt.priority,
            position: tt.position,
            user_id: userId,
            depends_on_positions: tt.depends_on_positions || [],
            dependency_type: tt.dependency_type || 'none',
            dependency_group: tt.dependency_group || null,
            offset_from_position: tt.offset_from_position,
            offset_days: tt.offset_days || 0,
            offset_type: tt.offset_type || 'none',
          });
        }
      }

      // 2) Goals (deliverables)
      const oldToNewGoal = new Map<string, string>();
      const goals: Goal[] = data.goals || [];
      for (const g of goals) {
        const created = await db.goals.create({
          user_id: userId,
          title: g.title,
          description: g.description,
          category: g.category,
          priority: g.priority,
          year: g.year,
          position: g.position || 0,
          matter_id: null,
          project_id: null,
          client_id: null,
          start_date: g.start_date || null,
          deadline: g.deadline || null,
        } as any);
        oldToNewGoal.set(g.id as string, created.id);
      }

      // 3) Tasks
      const oldToNewTask = new Map<string, string>();
      const tasks: Task[] = data.tasks || [];
      for (const t of tasks) {
        const created = await db.tasks.create({
          goal_id: t.goal_id ? (oldToNewGoal.get(t.goal_id as string) || null) : null,
          user_id: userId,
          title: t.title,
          description: t.description,
          priority: t.priority || 'Medium',
          status: t.status || 'To Do',
          task_type: t.task_type || 'To Do',
          assigned_to: null,
          deadline: t.deadline || null,
          start_date: t.start_date ? new Date(t.start_date) : new Date(),
          end_date: null,
          position: t.position || 0,
          is_regular: !!t.is_regular,
          frequency: t.frequency || (t.is_regular ? 'Daily' : 'None'),
          last_generated_date: null,
          next_due_date: t.next_due_date || null,
          recurrence_day: t.recurrence_day || null,
          depends_on: [],
          is_live: true,
          dependency_type: 'none',
          dependency_group: null,
          sop: null,
        } as any);
        oldToNewTask.set(t.id as string, created.id);
      }

      // 4) Time entries
      const timeEntries: Record<string, TimeEntry[]> = data.timeEntries || {};
      for (const [oldTaskId, entries] of Object.entries(timeEntries)) {
        const newTaskId = oldToNewTask.get(oldTaskId);
        if (!newTaskId) continue;
        for (const te of entries) {
          await db.timeEntries.create({
            task_id: newTaskId,
            user_id: userId,
            start_time: te.start_time ? new Date(te.start_time) : new Date(),
            end_time: te.end_time ? new Date(te.end_time) : new Date(),
            notes: te.notes || null,
          } as any);
        }
      }

      toast.success('All data imported. You may need to refresh views.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to import data');
    } finally {
      setBusy(false);
      setFileC(null);
    }
  }, [fileC, userId]);

  return (
    <div className="space-y-6">
      {/* Friendly guidance for non-technical users */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-900 flex items-start gap-3">
        <Info className="w-5 h-5 mt-0.5" />
        <div>
          <div className="font-semibold">Export and Import Your Data</div>
          <div className="text-sm">
            Use these buttons to download backups to your computer, or to restore from a file you previously saved.
            Imported items are added to your account — nothing gets deleted.
          </div>
        </div>
      </div>

      {/* Templates card */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="font-semibold text-gray-900 mb-1">Template Deliverables</div>
        <div className="text-sm text-gray-600 mb-3">Goal templates and their task templates.</div>
        <div className="flex flex-wrap gap-3 items-center">
          <button disabled={busy} onClick={handleExportTemplates} className="inline-flex items-center px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
            <Download className="w-4 h-4 mr-2" /> Export Templates
          </button>
          <label className="inline-flex items-center gap-2 text-sm">
            <Upload className="w-4 h-4" />
            <input disabled={busy} type="file" accept="application/json" onChange={e => setFileA(e.target.files?.[0] || null)} />
          </label>
          <button disabled={busy || !fileA} onClick={handleImportTemplates} className="inline-flex items-center px-3 py-2 bg-gray-800 text-white rounded-md disabled:opacity-50">
            Import Templates
          </button>
        </div>
      </div>

      {/* Regular tasks card */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="font-semibold text-gray-900 mb-1">Regular Tasks</div>
        <div className="text-sm text-gray-600 mb-3">Daily/recurring tasks only. Use this for lightweight backups.</div>
        <div className="flex flex-wrap gap-3 items-center">
          <button disabled={busy} onClick={handleExportRegular} className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">
            <Download className="w-4 h-4 mr-2" /> Export Regular Tasks
          </button>
          <label className="inline-flex items-center gap-2 text-sm">
            <Upload className="w-4 h-4" />
            <input disabled={busy} type="file" accept="application/json" onChange={e => setFileB(e.target.files?.[0] || null)} />
          </label>
          <button disabled={busy || !fileB} onClick={handleImportRegular} className="inline-flex items-center px-3 py-2 bg-gray-800 text-white rounded-md disabled:opacity-50">
            Import Regular Tasks
          </button>
        </div>
      </div>

      {/* All data card */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="font-semibold text-gray-900 mb-1">All My Data</div>
        <div className="text-sm text-gray-600 mb-3">Templates, deliverables (goals), tasks, and time entries.</div>
        <div className="flex flex-wrap gap-3 items-center">
          <button disabled={busy} onClick={handleExportAll} className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            <Download className="w-4 h-4 mr-2" /> Export All Data
          </button>
          <label className="inline-flex items-center gap-2 text-sm">
            <Upload className="w-4 h-4" />
            <input disabled={busy} type="file" accept="application/json" onChange={e => setFileC(e.target.files?.[0] || null)} />
          </label>
          <button disabled={busy || !fileC} onClick={handleImportAll} className="inline-flex items-center px-3 py-2 bg-gray-800 text-white rounded-md disabled:opacity-50">
            Import All Data
          </button>
        </div>

        <div className="flex items-start gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 mt-3 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>
            Imports add new items and don’t remove your existing data. If you import the same file twice, you’ll see duplicates.
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExportImportSettings;

